import useDrawStore, { TOOL_TYPES } from '@/stores/drawStore';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  MousePointer2,
  Hand,
  Square,
  Diamond,
  Circle,
  Minus,
  ArrowRight,
  Type,
  Pencil,
  Eraser,
  Link2,
  Undo2,
  Redo2,
  ZoomIn,
  ZoomOut,
  Maximize2,
  Grid3X3,
  Magnet,
  Lock,
  Unlock,
  Copy,
  Trash2,
  Group,
  Ungroup,
  ChevronUp,
  ChevronDown,
  ImageIcon,
  BoxSelect,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { useRef } from 'react';

const tools = [
  { id: TOOL_TYPES.SELECT, icon: MousePointer2, label: 'Sélection', shortcut: 'V' },
  { id: TOOL_TYPES.HAND, icon: Hand, label: 'Déplacer vue', shortcut: 'H' },
  { id: 'separator1', type: 'separator' },
  { id: TOOL_TYPES.RECTANGLE, icon: Square, label: 'Rectangle', shortcut: 'R' },
  { id: TOOL_TYPES.DIAMOND, icon: Diamond, label: 'Losange', shortcut: 'D' },
  { id: TOOL_TYPES.CIRCLE, icon: Circle, label: 'Cercle', shortcut: 'C' },
  { id: TOOL_TYPES.LINE, icon: Minus, label: 'Ligne', shortcut: 'L' },
  { id: TOOL_TYPES.ARROW, icon: ArrowRight, label: 'Flèche', shortcut: 'A' },
  { id: TOOL_TYPES.CONNECTOR, icon: Link2, label: 'Connecteur', shortcut: 'X' },
  { id: 'separator2', type: 'separator' },
  { id: TOOL_TYPES.TEXT, icon: Type, label: 'Texte', shortcut: 'T' },
  { id: TOOL_TYPES.FREEHAND, icon: Pencil, label: 'Dessin libre', shortcut: 'P' },
  { id: TOOL_TYPES.IMAGE, icon: ImageIcon, label: 'Importer image', shortcut: 'I' },
  { id: TOOL_TYPES.ERASER, icon: Eraser, label: 'Gomme', shortcut: 'E' },
];

export default function Toolbar() {
  const {
    activeTool,
    setActiveTool,
    selectedIds,
    showGrid,
    snapToGrid,
    zoom,
    undo,
    redo,
    zoomIn,
    zoomOut,
    resetZoom,
    toggleGrid,
    toggleSnap,
    deleteSelected,
    duplicateElements,
    lockSelected,
    groupSelected,
    ungroupSelected,
    bringToFront,
    sendToBack,
    copy,
    paste,
    history,
    historyIndex,
    addElement,
    selectAll,
  } = useDrawStore();
  
  const imageInputRef = useRef(null);

  const hasSelection = selectedIds.length > 0;
  const canUndo = historyIndex > 0;
  const canRedo = historyIndex < history.length - 1;
  
  // Handle image import
  const handleImageImport = (e) => {
    const file = e.target.files?.[0];
    if (!file || !file.type.startsWith('image/')) return;
    
    const reader = new FileReader();
    reader.onload = (event) => {
      const img = new Image();
      img.onload = () => {
        // Calculate size maintaining aspect ratio
        const maxSize = 300;
        let width = img.width;
        let height = img.height;
        
        if (width > maxSize || height > maxSize) {
          if (width > height) {
            height = (height / width) * maxSize;
            width = maxSize;
          } else {
            width = (width / height) * maxSize;
            height = maxSize;
          }
        }
        
        addElement('image', {
          x: 100 + Math.random() * 200,
          y: 100 + Math.random() * 200,
          width,
          height,
          imageUrl: event.target.result,
        });
      };
      img.src = event.target.result;
    };
    reader.readAsDataURL(file);
    
    // Reset input
    e.target.value = '';
  };
  
  // Handle tool click
  const handleToolClick = (toolId) => {
    if (toolId === TOOL_TYPES.IMAGE) {
      imageInputRef.current?.click();
    } else {
      setActiveTool(toolId);
    }
  };

  return (
    <TooltipProvider delayDuration={200}>
      <div className="flex flex-col gap-2 p-2 bg-background border-r border-border h-full">
        {/* Hidden file input for image import */}
        <input
          type="file"
          ref={imageInputRef}
          className="hidden"
          accept="image/*"
          onChange={handleImageImport}
        />
        
        {/* Drawing Tools */}
        <div className="flex flex-col gap-1">
          {tools.map((tool) => {
            if (tool.type === 'separator') {
              return <Separator key={tool.id} className="my-1" />;
            }

            const Icon = tool.icon;
            const isActive = activeTool === tool.id;

            return (
              <Tooltip key={tool.id}>
                <TooltipTrigger asChild>
                  <Button
                    variant={isActive ? 'default' : 'ghost'}
                    size="icon"
                    className={cn(
                      'h-10 w-10',
                      isActive && 'bg-primary text-primary-foreground'
                    )}
                    onClick={() => handleToolClick(tool.id)}
                    data-testid={`tool-${tool.id}`}
                  >
                    <Icon className="h-5 w-5" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="right">
                  <p>{tool.label} <span className="text-muted-foreground ml-1">({tool.shortcut})</span></p>
                </TooltipContent>
              </Tooltip>
            );
          })}
        </div>

        <Separator className="my-2" />

        {/* History */}
        <div className="flex flex-col gap-1">
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="h-10 w-10"
                onClick={undo}
                disabled={!canUndo}
                data-testid="btn-undo"
              >
                <Undo2 className="h-5 w-5" />
              </Button>
            </TooltipTrigger>
            <TooltipContent side="right">
              <p>Annuler <span className="text-muted-foreground ml-1">(Ctrl+Z)</span></p>
            </TooltipContent>
          </Tooltip>

          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="h-10 w-10"
                onClick={redo}
                disabled={!canRedo}
                data-testid="btn-redo"
              >
                <Redo2 className="h-5 w-5" />
              </Button>
            </TooltipTrigger>
            <TooltipContent side="right">
              <p>Rétablir <span className="text-muted-foreground ml-1">(Ctrl+Y)</span></p>
            </TooltipContent>
          </Tooltip>
        </div>

        <Separator className="my-2" />

        {/* Zoom */}
        <div className="flex flex-col gap-1">
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="h-10 w-10"
                onClick={zoomIn}
                data-testid="btn-zoom-in"
              >
                <ZoomIn className="h-5 w-5" />
              </Button>
            </TooltipTrigger>
            <TooltipContent side="right">
              <p>Zoom + <span className="text-muted-foreground ml-1">(Ctrl++)</span></p>
            </TooltipContent>
          </Tooltip>

          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="h-10 w-10 text-xs font-medium"
                onClick={resetZoom}
              >
                {Math.round(zoom * 100)}%
              </Button>
            </TooltipTrigger>
            <TooltipContent side="right">
              <p>Réinitialiser le zoom</p>
            </TooltipContent>
          </Tooltip>

          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="h-10 w-10"
                onClick={zoomOut}
                data-testid="btn-zoom-out"
              >
                <ZoomOut className="h-5 w-5" />
              </Button>
            </TooltipTrigger>
            <TooltipContent side="right">
              <p>Zoom - <span className="text-muted-foreground ml-1">(Ctrl+-)</span></p>
            </TooltipContent>
          </Tooltip>
        </div>

        <Separator className="my-2" />

        {/* View Options */}
        <div className="flex flex-col gap-1">
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant={showGrid ? 'default' : 'ghost'}
                size="icon"
                className={cn('h-10 w-10', showGrid && 'bg-primary/20 text-primary')}
                onClick={toggleGrid}
                data-testid="btn-grid"
              >
                <Grid3X3 className="h-5 w-5" />
              </Button>
            </TooltipTrigger>
            <TooltipContent side="right">
              <p>Grille {showGrid ? '(activée)' : '(désactivée)'}</p>
            </TooltipContent>
          </Tooltip>

          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant={snapToGrid ? 'default' : 'ghost'}
                size="icon"
                className={cn('h-10 w-10', snapToGrid && 'bg-primary/20 text-primary')}
                onClick={toggleSnap}
                data-testid="btn-snap"
              >
                <Magnet className="h-5 w-5" />
              </Button>
            </TooltipTrigger>
            <TooltipContent side="right">
              <p>Magnétisme {snapToGrid ? '(activé)' : '(désactivé)'}</p>
            </TooltipContent>
          </Tooltip>
        </div>

        <Separator className="my-2" />

        {/* Select All */}
        <div className="flex flex-col gap-1">
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="h-10 w-10"
                onClick={selectAll}
                data-testid="btn-select-all"
              >
                <BoxSelect className="h-5 w-5" />
              </Button>
            </TooltipTrigger>
            <TooltipContent side="right">
              <p>Sélectionner tout <span className="text-muted-foreground ml-1">(Ctrl+A)</span></p>
            </TooltipContent>
          </Tooltip>
        </div>

        {/* Selection Actions */}
        {hasSelection && (
          <>
            <Separator className="my-2" />
            <div className="flex flex-col gap-1">
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-10 w-10"
                    onClick={copy}
                    data-testid="btn-copy"
                  >
                    <Copy className="h-5 w-5" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="right">
                  <p>Copier <span className="text-muted-foreground ml-1">(Ctrl+C)</span></p>
                </TooltipContent>
              </Tooltip>

              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-10 w-10"
                    onClick={() => duplicateElements(selectedIds)}
                    data-testid="btn-duplicate"
                  >
                    <Copy className="h-5 w-5" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="right">
                  <p>Dupliquer <span className="text-muted-foreground ml-1">(Ctrl+D)</span></p>
                </TooltipContent>
              </Tooltip>

              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-10 w-10 text-destructive hover:text-destructive"
                    onClick={deleteSelected}
                    data-testid="btn-delete"
                  >
                    <Trash2 className="h-5 w-5" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="right">
                  <p>Supprimer <span className="text-muted-foreground ml-1">(Suppr)</span></p>
                </TooltipContent>
              </Tooltip>

              <Separator className="my-1" />

              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-10 w-10"
                    onClick={lockSelected}
                    data-testid="btn-lock"
                  >
                    <Lock className="h-5 w-5" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="right">
                  <p>Verrouiller</p>
                </TooltipContent>
              </Tooltip>

              {selectedIds.length > 1 && (
                <>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-10 w-10"
                        onClick={groupSelected}
                        data-testid="btn-group"
                      >
                        <Group className="h-5 w-5" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent side="right">
                      <p>Grouper <span className="text-muted-foreground ml-1">(Ctrl+G)</span></p>
                    </TooltipContent>
                  </Tooltip>

                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-10 w-10"
                        onClick={ungroupSelected}
                        data-testid="btn-ungroup"
                      >
                        <Ungroup className="h-5 w-5" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent side="right">
                      <p>Dégrouper <span className="text-muted-foreground ml-1">(Ctrl+Shift+G)</span></p>
                    </TooltipContent>
                  </Tooltip>
                </>
              )}

              <Separator className="my-1" />

              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-10 w-10"
                    onClick={bringToFront}
                    data-testid="btn-front"
                  >
                    <ChevronUp className="h-5 w-5" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="right">
                  <p>Premier plan</p>
                </TooltipContent>
              </Tooltip>

              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-10 w-10"
                    onClick={sendToBack}
                    data-testid="btn-back"
                  >
                    <ChevronDown className="h-5 w-5" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="right">
                  <p>Arrière-plan</p>
                </TooltipContent>
              </Tooltip>
            </div>
          </>
        )}

        {/* Spacer */}
        <div className="flex-1" />
      </div>
    </TooltipProvider>
  );
}
